/**
 * A pedal-bound TOGGLE aimed at other computers (audit 2026-11-03 A-11,
 * operator 2026-11-03: "route via mesh").
 *
 * A pedal's rule is armed on the default computer (`ruleSync.ts`), and a
 * toggle's rule used to flip the toggle THERE. A toggle aimed at STREAMPC is
 * tapped on STREAMPC (FireButton flips its targets), so a stomp flipped a
 * second, separate copy on the default and sent its MIDI from the wrong
 * computer: the light on the button and the instrument disagreed.
 *
 * Such a toggle is registered as a named action on each of its computers, the
 * way a controller-bound control is (`modShare/sync.ts`: `dispatch toggle.flip`
 * of that computer's own toggle), and its rule FIRES that action
 * (`{ module: "action", command: "fire" }`). OPBridge runs a named action on the
 * computers that hold it (`action_mesh.rs`), so a stomp lands exactly where a
 * tap does. A toggle aimed only at the rule's own computer keeps its plain
 * `toggle.flip`: nothing changes for the common case.
 *
 * Its own module: `modShare/ops.ts` imports `ruleSync.ts`, so neither may
 * import the other for this.
 */

import { activePedals, buttonEmitsMidi, type LayoutItem } from '../layout/projectStore.ts';
import { resolveBridges } from '../opbridge/store.ts';
import type { BridgeConfig } from '../opbridge/storage.ts';
import { pedalBridgeRowId } from '../pedals/bridgeBinding.ts';

/** The computers a button's tap goes to: its pedal's computer, else its "Sends to". */
function tapTargets(item: Extract<LayoutItem, { kind: 'button' }>): BridgeConfig[] {
  const ids = item.pedalId ? [pedalBridgeRowId(activePedals().find((p) => p.id === item.pedalId)) ?? ''] : (item.bridgeIds ?? ['']);
  const seen = new Map<string, BridgeConfig>();
  for (const t of ids) for (const b of resolveBridges(t, { ignorePanicLatch: true })) seen.set(b.id, b);
  return [...seen.values()];
}

/** True when `item` is a MIDI-bound toggle whose taps go anywhere but `ruleBridge`
 *  alone: its rule fires its named action, and the share sync registers it. */
export function pedalToggleViaMesh(item: LayoutItem, ruleBridge: BridgeConfig | null): boolean {
  if (item.kind !== 'button' || !item.midiTrigger) return false;
  if ((item.mode ?? 'toggle') !== 'toggle') return false;
  const action = item.actionType ?? 'cc';
  if (action === 'pc' || !buttonEmitsMidi(action)) return false;
  const t = tapTargets(item);
  if (t.length === 0) return false;
  return !(t.length === 1 && !!ruleBridge && t[0]!.id === ruleBridge.id);
}
